<template>
  <div
    class="cf-section cf-enter rounded-lg border bg-gray-50 transition-all"
    :class="
      expanded
        ? 'border-gray-300'
        : 'border-gray-300 hover:border-gray-400 hover:shadow-sm'
    "
    :data-cf-section-key="modelValue._key"
  >
    <div
      class="flex items-center gap-2 px-3 py-2"
      :class="{ 'border-b border-gray-200': expanded }"
    >
      <i
        class="cf-section-handle fas fa-grip-vertical cursor-move text-gray-300 hover:text-gray-500"
        data-cy="section-handle"
      ></i>
      <button
        type="button"
        class="flex shrink-0 items-center gap-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        :aria-expanded="expanded"
        data-cy="section-expand"
        @click="expanded = !expanded"
      >
        <i
          class="fas fa-chevron-right transition-transform duration-200"
          :class="{ 'rotate-90': expanded }"
        ></i>
        <span
          class="text-xs font-semibold uppercase tracking-wide text-gray-400"
          >Section</span
        >
      </button>
      <InputField
        :model-value="modelValue.name"
        inline
        placeholder="Section name (e.g. Arrival, Safety)"
        :validation="nameTouched && !modelValue.name ? 'error' : 'none'"
        class="flex-1"
        data-cy="section-name"
        @focusout="nameTouched = true"
        @update:model-value="(v) => emitUpdate({ name: v })"
      />
      <span
        v-if="!expanded"
        class="whitespace-nowrap text-xs text-gray-500"
        data-cy="section-question-count"
        >{{ modelValue.questions.length }}
        {{ modelValue.questions.length === 1 ? "question" : "questions" }}</span
      >
      <div class="flex items-center gap-1">
        <button
          type="button"
          :class="CF_ACTION_BTN"
          title="Duplicate section"
          data-cy="section-duplicate"
          @click="$emit('duplicate')"
        >
          <i class="fas fa-clone"></i>
        </button>
        <button
          type="button"
          :class="CF_ACTION_BTN"
          :disabled="index === 0"
          title="Move up"
          data-cy="section-move-up"
          @click="$emit('move-up')"
        >
          <i class="fas fa-arrow-up"></i>
        </button>
        <button
          type="button"
          :class="CF_ACTION_BTN"
          :disabled="index === total - 1"
          title="Move down"
          data-cy="section-move-down"
          @click="$emit('move-down')"
        >
          <i class="fas fa-arrow-down"></i>
        </button>
        <button
          type="button"
          :class="[CF_ACTION_BTN, 'hover:!text-red-600']"
          title="Remove section"
          data-cy="section-remove"
          @click="requestRemove"
        >
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <div class="cf-collapse" :class="{ 'is-open': expanded }">
      <div :inert="!expanded">
        <div class="p-3">
          <div class="mb-3">
            <label
              class="mb-1 block text-sm font-medium text-gray-700"
              :for="`cf-section-desc-${modelValue._key}`"
              >Description</label
            >
            <InputField
              :id="`cf-section-desc-${modelValue._key}`"
              :model-value="modelValue.description"
              inline
              placeholder="Shown to the tech and on the PDF"
              data-cy="section-description"
              @update:model-value="(v) => emitUpdate({ description: v })"
            />
          </div>
          <!--
            No shared drag group: questions reorder only within their own
            section. A cross-section move would send the question's id nested
            under a section that doesn't own it, and nested attributes resolves
            an incoming id against that section's own questions — so it raises
            RecordNotFound (surfaced as 404) regardless of what the source
            section sends. Expressing a move at all would mean creating the
            question anew in the target and _destroying it in the source.
          -->
          <Draggable
            :model-value="modelValue.questions"
            item-key="_key"
            handle=".cf-question-handle"
            :animation="180"
            ghost-class="cf-ghost"
            chosen-class="cf-chosen"
            class="space-y-2"
            data-cy="questions-draggable"
            @update:model-value="(questions) => emitUpdate({ questions })"
          >
            <template #item="{ element, index: qIndex }">
              <CustomFormQuestionEditor
                :model-value="element"
                :index="qIndex"
                :total="modelValue.questions.length"
                :active-question-key="activeQuestionKey"
                @update:model-value="
                  (question) => updateQuestion(qIndex, question)
                "
                @activate="(key) => $emit('activate', key)"
                @toggle="(key, isOpen) => $emit('toggle', key, isOpen)"
                @remove="removeQuestion(qIndex)"
                @duplicate="duplicateQuestion(qIndex)"
                @move-up="moveQuestion(qIndex, -1)"
                @move-down="moveQuestion(qIndex, 1)"
              />
            </template>
          </Draggable>

          <div class="mt-3">
            <CustomFormAddItem @add="addQuestion" />
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showDeleteModal" title="Delete section?" size="small">
      <p class="text-sm text-gray-600">
        Delete
        <span class="font-medium">{{ modelValue.name || "this section" }}</span>
        and its {{ modelValue.questions.length }}
        {{ modelValue.questions.length === 1 ? "question" : "questions" }}? This
        can't be undone.
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 px-4 py-3">
          <SeraButton
            text="Cancel"
            type="secondary"
            data-cy="section-delete-cancel"
            @click="showDeleteModal = false"
          />
          <SeraButton
            text="Delete section"
            type="danger"
            data-cy="section-delete-confirm"
            @click="confirmRemove"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import Draggable from "vuedraggable";

import SeraButton from "@/components/Button.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import CustomFormAddItem from "./CustomFormAddItem.vue";
import {
  type BuilderQuestion,
  type BuilderSection,
  CF_ACTION_BTN,
  cloneQuestion,
  newQuestion
} from "./customFormFields";
import CustomFormQuestionEditor from "./CustomFormQuestionEditor.vue";

const props = withDefaults(
  defineProps<{
    modelValue: BuilderSection;
    index?: number;
    total?: number;
    expandCommand?: { expand: boolean; nonce: number };
    activeQuestionKey?: string | null;
  }>(),
  { index: 0, total: 1, expandCommand: undefined, activeQuestionKey: null }
);
const emit = defineEmits<{
  "update:modelValue": [value: BuilderSection];
  activate: [key: string];
  toggle: [key: string, expanded: boolean];
  remove: [];
  duplicate: [];
  "move-up": [];
  "move-down": [];
}>();

// Loaded sections (already named) start collapsed so a big form reads as an
// outline; a freshly added, unnamed section starts open so the admin can fill
// it right away. Expand/collapse-all from the toolbar overrides this on demand.
const expanded = ref(!props.modelValue.name);
const nameTouched = ref(false);
const showDeleteModal = ref(false);
watch(
  () => props.expandCommand?.nonce,
  () => {
    if (props.expandCommand) expanded.value = props.expandCommand.expand;
  }
);

function emitUpdate(changes: Partial<BuilderSection>) {
  emit("update:modelValue", { ...props.modelValue, ...changes });
}

// A section can hold many questions, so guard the one-click delete with a
// confirmation modal; empty sections delete straight away.
function requestRemove() {
  if (props.modelValue.questions.length > 0) {
    showDeleteModal.value = true;
    return;
  }
  emit("remove");
}

function confirmRemove() {
  showDeleteModal.value = false;
  emit("remove");
}

function updateQuestion(index: number, question: BuilderQuestion) {
  const questions = props.modelValue.questions.map((existing, i) =>
    i === index ? question : existing
  );
  emitUpdate({ questions });
}

function addQuestion(type: string) {
  emitUpdate({
    questions: [...props.modelValue.questions, newQuestion(type)]
  });
}

function duplicateQuestion(index: number) {
  const questions = [...props.modelValue.questions];
  questions.splice(index + 1, 0, cloneQuestion(questions[index]));
  emitUpdate({ questions });
}

function moveQuestion(index: number, delta: number) {
  const target = index + delta;
  if (target < 0 || target >= props.modelValue.questions.length) return;
  const questions = [...props.modelValue.questions];
  [questions[index], questions[target]] = [questions[target], questions[index]];
  emitUpdate({ questions });
}

// Removing a persisted question records its id so the next save can send
// `_destroy: true` for it; unsaved questions just drop out of the tree.
function removeQuestion(index: number) {
  const removed = props.modelValue.questions[index];
  const questions = props.modelValue.questions.filter((_, i) => i !== index);
  const removedQuestionIds = removed.id
    ? [...props.modelValue.removedQuestionIds, removed.id]
    : props.modelValue.removedQuestionIds;
  emitUpdate({ questions, removedQuestionIds });
}
</script>

<style scoped>
.cf-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 220ms ease;
}
.cf-collapse.is-open {
  grid-template-rows: 1fr;
}
.cf-collapse > div {
  overflow: hidden;
}

/* Brief ring pulse when the outline navigator jumps to this section. */
.cf-jump-pulse {
  animation: cf-jump-pulse 1200ms ease;
}
@keyframes cf-jump-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
  }
  100% {
    box-shadow: 0 0 0 12px rgba(59, 130, 246, 0);
  }
}

.cf-enter {
  animation: cf-enter 200ms ease;
}
@keyframes cf-enter {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Drag feedback for the questions list. */
.cf-ghost {
  opacity: 0.4;
}
.cf-chosen {
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
}

@media (prefers-reduced-motion: reduce) {
  .cf-collapse,
  .cf-enter,
  .cf-section,
  .cf-jump-pulse {
    transition: none;
    animation: none;
  }
}
</style>
